import {FC, useState} from "react"
import {useTranslation} from "react-i18next"
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/20/solid"
import {differenceInCalendarDays} from "date-fns/differenceInCalendarDays"
import {startOfMonth} from "date-fns/startOfMonth"
import {twMerge} from "tailwind-merge"

import {getCurrentLanguage} from "../i18n"

const isToday = (someDate: Date, today = new Date()) => {
  return (
    someDate.getDate() == today.getDate() &&
    someDate.getMonth() == today.getMonth() &&
    someDate.getFullYear() == today.getFullYear()
  )
}

const getMonday = (date: Date) => {
  date = new Date(date)
  const day = date.getDay(),
    diff = date.getDate() - day + (day == 0 ? -6 : 1) // adjust when day is sunday
  return new Date(date.setDate(diff))
}

const addDays = (date: Date, days: number) => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

const addMonths = (date: Date, months: number) => {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

const generateDays = (from: Date) => {
  const first = getMonday(from)
  const days: Date[] = []
  for (let i = 0; i < 7 * 6; i++) {
    days.push(addDays(first, i))
  }
  return days
}

const Weekdays = () => {
  const monday = getMonday(new Date())
  return (
    <div className={"mt-6 grid grid-cols-7 text-xs leading-6 text-cr-grey-50"}>
      {new Array(7).fill(undefined).map((_, i) => (
        <div key={i} className={twMerge(i > 4 && "text-cr-blue")}>
          {addDays(monday, i).toLocaleString(getCurrentLanguage(), {weekday: "short"})}
        </div>
      ))}
    </div>
  )
}

interface ICalendar {
  value?: Date
  onSelect?: (date: Date) => void
  beginning?: Date
  minDate?: Date
  maxDate?: Date
}

const Calendar: FC<ICalendar> = ({value, onSelect, beginning = new Date(), minDate, maxDate}) => {
  const {t} = useTranslation()

  const [firstDate, setFirstDate] = useState(() => startOfMonth(beginning))
  const days = generateDays(firstDate)

  const isCurrentMonth = (date: Date) => date.getMonth() === firstDate.getMonth()
  const isSelected = (date: Date) => isToday(date, value)
  const isOutOfRange = (date: Date) =>
    (minDate !== undefined && differenceInCalendarDays(date, minDate) < 0) ||
    (maxDate !== undefined && differenceInCalendarDays(date, maxDate) > 0)

  return (
    <div className={"text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 xl:col-start-9"}>
      <div className={"flex items-center text-cr-grey-50"}>
        <button
          type={"button"}
          className={
            "-m-1.5 flex flex-none items-center justify-center p-1.5 text-cr-grey-15 transition-colors hover:text-cr-grey-50"
          }
          onClick={() => setFirstDate(old => addMonths(old, -1))}
        >
          <span className={"sr-only"}>{t("Calendar_Previous month")}</span>
          <ChevronLeftIcon className={"h-5 w-5"} aria-hidden={"true"} />
        </button>
        <div className={"flex-auto text-sm font-semibold"}>
          {firstDate.toLocaleString(getCurrentLanguage(), {month: "long"})}{" "}
          {firstDate.getFullYear() !== new Date().getFullYear() && firstDate.getFullYear()}
        </div>
        <button
          type={"button"}
          className={
            "-m-1.5 flex flex-none items-center justify-center p-1.5 text-cr-grey-15 transition-colors hover:text-cr-grey-50"
          }
          onClick={() => setFirstDate(old => addMonths(old, 1))}
        >
          <span className={"sr-only"}>{t("Calendar_Next month")}</span>
          <ChevronRightIcon className={"h-5 w-5"} aria-hidden={"true"} />
        </button>
      </div>
      <Weekdays />
      <div
        className={
          "isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-cr-grey-5 text-sm shadow-sm ring-1 ring-cr-grey-15"
        }
      >
        {days.map((date, i) => (
          <button
            key={date.toString()}
            type={"button"}
            disabled={isOutOfRange(date)}
            className={twMerge(
              "py-1.5 focus:z-10",
              !isOutOfRange(date) && (isToday(date) ? "hover:bg-cr-blue-super-light" : "hover:bg-cr-grey-5"),
              isToday(date) ? "bg-cr-blue-super-light" : isCurrentMonth(date) ? "bg-white" : "bg-cr-grey-5",
              (isSelected(date) || isToday(date)) && "font-bold",
              isSelected(date) && "text-white",
              !isSelected(date) && isCurrentMonth(date) && !isToday(date) && "text-cr-grey-80",
              !isSelected(date) && !isCurrentMonth(date) && !isToday(date) && "text-cr-grey-50",
              isToday(date) && !isSelected(date) && "text-cr-blue",
              i === 0 && "rounded-tl-lg",
              i === 6 && "rounded-tr-lg",
              i === days.length - 7 && "rounded-bl-lg",
              i === days.length - 1 && "rounded-br-lg",
              isOutOfRange(date) && "cursor-not-allowed text-cr-grey-15"
            )}
            onClick={() => !isOutOfRange(date) && onSelect?.(date)}
          >
            <time
              dateTime={date.toString()}
              className={twMerge(
                "mx-auto flex h-7 w-7 items-center justify-center rounded-full",
                isSelected(date) && (isToday(date) ? "bg-cr-blue" : "bg-cr-grey-80")
              )}
            >
              {date.getDate()}
            </time>
          </button>
        ))}
      </div>
    </div>
  )
}

export default Calendar
